import { Container } from '../components/container';
import { Columns, Column } from '../components/columns';
import { AnimatedText } from '../components/animated-text';
import { Vine } from '../components/vine';
import { useVisibleDelay } from '../hooks/use-delay';
import { Hero } from '../components/hero';
import { BizAid } from '../components/svgs/bizaid';
import { BizAidMobile } from '../components/svgs/bizaid-mobile';

export default function CovidResponse(props) {
  const [ref, animateIntroSection] = useVisibleDelay();
  return (
    <>
      <Hero />
      <article className="page narrow">
        <Container>
          <Columns>
            <Column className="one-third-ish">
              <h1 ref={ref} className="undervine">
                <span>
                  <AnimatedText isVisible={animateIntroSection}>
                    COVID-19 Response
                  </AnimatedText>
                  {animateIntroSection && <Vine />}
                </span>
              </h1>
            </Column>
            <Column className="two-thirds-ish">
              <p>
                With the support of numerous funding and community partners, the
                Northland Foundation disbursed{' '}
                <strong>
                  more than $4.6 million in COVID-19 response funds
                </strong>
                . Response took three main forms: targeted grantmaking,
                administering grants and technical assistance for small
                businesses, and providing ongoing support for child care.
              </p>
            </Column>
          </Columns>
        </Container>

        <Container isDark>
          <h3 className="undervine">
            <span>
              Targeted Response Grants
              <Vine />
            </span>
          </h3>
          <p>
            During the past fiscal year,{' '}
            <strong>67 grants totaling $752,186</strong> were awarded to
            nonprofits, Tribal nations, school districts, and other
            community-based organizations responding to pandemic-related needs.
          </p>
          <p>
            All but four were{' '}
            <strong>Emergency Early Childhood Wrap-around Care Grants</strong>{' '}
            to support children ages 0-8 disproportionately impacted by effects
            of the pandemic: children in rural communities, with disabilities,
            from low-income households, who are Black, Indigenous, or other
            people of color, in foster care, and other historically underserved
            children. Wrap-around Care Grants were directed to children’s
            social-emotional health, online and distance learning, enrichment
            programs, and more, in partnership with the{' '}
            <a
              href="https://education.mn.gov/MDE/index.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Minnesota Department of Education
            </a>{' '}
            on behalf of the{' '}
            <a
              href="https://www.ohe.state.mn.us/mPg.cfm?pageID=2410"
              target="_blank"
              rel="noopener noreferrer"
            >
              Governor’s Emergency Education Relief (GEER) Fund
            </a>
            .
          </p>
          <p>
            The remaining $219,000 was awarded to{' '}
            <strong>
              organizations led by Black, Indigenous, and other people of color
            </strong>{' '}
            to help them provide supports and services to community members
            during the pandemic.
          </p>

          <details>
            <summary>View Relief Grants</summary>
            <div>
              <h5>Duluth NAACP, Duluth $15,000</h5>
              <p>
                To support a needs assessment to improve outreach, education,
                and access to health services for Black, Indigenous, and
                Communities of Color in the Duluth area during the COVID-19
                pandemic.
              </p>

              <h5>
                Grand Portage Band of Lake Superior Chippewa, Grand Portage
                $100,000
              </h5>
              <p>
                To support COVID-19 relief and recovery efforts with grant
                funding provided through the Minnesota Disaster Recovery Fund.
              </p>

              <h5>Bois Forte Band of Chippewa, Nett Lake $100,000</h5>
              <p>
                To support COVID-19 relief and recovery efforts with grant
                funding provided through the Minnesota Disaster Recovery Fund.
              </p>
              <h5>Calvary Baptist Church, Duluth $4,000</h5>
              <p>
                To support outreach, health, and community-building efforts
                during the COVID-19 pandemic.
              </p>

              <h5>
                Emergency Early Childhood Wrap-Around Care, Northeast Minnesota
                School and Community Organizations $533,186
              </h5>
              <p>
                To support wrap-around services for children ages 0 to 8 years,
                especially children from historically underserved populations
                disproportionately impacted by the pandemic.
              </p>

              <table>
                <tbody>
                  <tr>
                    <td>Duluth Preschool, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$4,950</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Lake Superior School District - ISD #381, Two Harbors
                    </td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$15,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Wrenshall School District - ISD #100, Wrenshall</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$3,700</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Grand Rapids School District - ISD #318, Grand Rapids
                    </td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Center City Housing Corporation, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$15,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Duluth Area Family YMCA, Hermantown</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Caring and Sharing Daycare, Aitkin</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Hope for Kids Childcare Center, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$9,162</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Cook County Schools - ISD #166, Grand Marais</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$15,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Invest Early, Grand Rapids</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$15,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Lutheran Social Service of Minnesota, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$15,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Otis Family Daycare, Cloquet</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Churches United In Ministry, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$15,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>McGregor KIDS PLUS/Something Cool, Inc., McGregor</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$11,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Children of Grace Childcare & Education Center, Hibbing
                    </td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Cook County Community YMCA, Grand Marais</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Cook County Schools - ISD #166, Grand Marais</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>New Hope FOR Families, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>United Way of Carlton County, Cloquet</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Lake Superior School District - ISD #381, Two Harbors
                    </td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$7,500</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Itasca County Family YMCA, Grand Rapids</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Hope for Kids Childcare Center, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$7,500</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Proctor Public Schools - ISD #704, Proctor</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Esko Public Schools - ISD #099, Esko</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>YWCA of Duluth, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$14,970</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Lutheran Social Service of Minnesota, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Duluth Early Childhood Family Education, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Lake Superior Zoological Society, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Duluth Area Family YMCA, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$7,500</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Project READ, Grand Rapids</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>The Salvation Army Duluth, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Center City Housing Corporation, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Kiddy Karousel Child Care Center, Hibbing</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Lincoln Park Children and Families Collaborative, Duluth
                    </td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      South Koochiching-Rainy River School District - ISD #363,
                      Northome
                    </td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$12,060</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Fond du Lac Band of Lake Superior Chippewa, Cloquet</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$15,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Cook County Schools - ISD #166, Grand Marais</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$7,500</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Carlton County Children and Family Services Collaborative,
                      Carlton
                    </td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$15,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Duluth Area Family YMCA, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$7,500</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Cook County Community YMCA, Grand Marais</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$7,500</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Children of Grace Childcare & Education Center, Hibbing
                    </td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>YMCA Early Childhood Development Center, Hermantown</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$7,500</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Duluth Children's Museum, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Mesabi Family YMCA, Virginia</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$15,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Legacy of Excellence Foundation, Moose Lake</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Minisinaakwaang Leadership Academy, McGregor</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,400</p>
                    </td>
                  </tr>
                  <tr>
                    <td>YWCA of Duluth, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$4,400</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Bois Forte Band of Chippewa, Nett Lake</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Little Lambs Learning Center, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$4,944</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Lake Superior School District - ISD #381, Two Harbors
                    </td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Virginia School District #706, Virginia</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Grand Rapids School District - ISD #318, Grand Rapids
                    </td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Citizens for Backus, International Falls</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Lincoln Park Children and Families Collaborative, Duluth
                    </td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Cook County Early Childhood Coalition, Grand Marais</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>First Witness Child Advocacy Center, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$7,800</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Ely Community Resource, Inc., Ely</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Hibbing Public Library, Hibbing</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$5,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>McGregor Schools - ISD #004, McGregor</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Duluth Area YMCA, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Boys & Girls Clubs of the Northland, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$10,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Grand Portage Band of Lake Superior Chippewa, Grand
                      Portage
                    </td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$15,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Oshki Ogimaag Charter School, Grand Portage</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$4,300</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>
        </Container>

        <Container>
          <h3 className="undervine">
            <span>
              Aid For Small Businesses
              <Vine />
            </span>
          </h3>

          <p>
            During the summer of 2020, the{' '}
            <a
              href="https://mn.gov/deed/business/financing-business/deed-programs/relief/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Minnesota Small Business Relief Grants Program
            </a>{' '}
            was created as a legislative response to the pandemic to provide
            financial support to small businesses directly affected by the
            impacts of COVID-19. The program was overseen by the Minnesota
            Department of Employment and Economic Development, which named the
            Northland Foundation as a regional partner to help administer
            grants. Eligible businesses were selected to receive a grant through
            a state-directed lottery system. Our team processed{' '}
            <strong>
              381 grants of $10,000 each, for a total of $3.81 million awarded
              to northeastern Minnesota small businesses
            </strong>
            . The geographic spread of recipients in our region is shown below.
          </p>
          <p>
            In addition, as the regional host of the
            <a
              href="https://www.northlandsbdc.org/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {' '}
              Northland Small Business Development Center,
            </a>{' '}
            the Foundation helps support nine business consultants serving
            northeastern Minnesota. In 2020, this team of professionals clocked{' '}
            <strong>5,700 hours</strong> working with{' '}
            <strong>1,049 northeastern Minnesota clients</strong> (at no
            charge). They helped entrepreneurs in our region prepare plans and
            projections, start or sell a business, rethink business models and
            manage through unpredictable economic conditions, and apply for
            state and federal relief programs such as Shuttered Venues Operators
            Grants, Restaurant Revitalization Grants, and the Payroll Protection
            Program.
          </p>
          <p>
            <strong>
              Small Business Relief Grants, Northeastern Minnesota
            </strong>{' '}
            <strong>$3,810,000</strong>
          </p>

          <BizAid />
          <BizAidMobile />
        </Container>

        <Container isDark>
          <h3 className="undervine">
            <span>
              Support for Child Care
              <Vine />
            </span>
          </h3>

          <p>
            In late March of 2020, at the request of Governor Walz, the
            Northland Foundation helped establish the{' '}
            <a
              href="https://northlandfdn.org/news/news-releases/NF_emergency-child-care-grants-pr-03262020.pdf"
              target="_blank"
            >
              Emergency Child Care Grant Program
            </a>{' '}
            to offer interim financial support to child care providers serving
            the children of emergency and essential workers. By the end of June
            2020, Northland had awarded 184 grants to licensed family and
            center-based child care in northeastern Minnesota. As the pandemic
            continued, providers remained challenged by fluctuating attendance
            and the high costs of ensuring a safe, healthy child care
            environment. With further funding secured during this past fiscal
            year, the Northland Foundation was pleased to be able to award
            another <strong>53 Emergency Child Care grants totaling</strong>{' '}
            <strong>$84,900 </strong>to help support licensed child care
            providers and retain access for families in our region.
          </p>

          <details>
            <summary>View Emergency Child Care Grants</summary>
            <div>
              <table>
                <tbody>
                  <tr>
                    <td>Judi's Daycare, Carlton</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Jody's Daycare, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Debra Aqui, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Rasmusson Romper Room, Aurora</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Iron Range Tykes Learning Center, Mountain Iron</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$3,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Little Adventures Daycare, Cloquet</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Melinda Gulbranson, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Kids 1st Daycare, International Falls</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Wood City Preschool, LLC, Cloquet</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$3,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Colleen Paulsen, Cloquet</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Alyssa Haedrich's Child Care, Moose Lake</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Enchanted Garden, Cloquet</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Cathy's Daycare, Cloquet</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Judy's House, Cloquet</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Colleen's Daycare, Cloquet</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Andrea Anderson, Cloquet</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Erin Lunde's Family Daycare, Cloquet</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Kid's Corner, Cloquet</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$3,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Kathy's Little Stars, Esko</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Tender Loving Childcare, Cloquet</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Tykes N Tots Preschool & Childcare, Carlton</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Duluth Preschool of Fine Arts, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$3,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Miles of Smiles, Chisholm</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Kickstart Preschool, LLC, Two Harbors</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$3,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Kidz Zone Daycare Center, Aitkin</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$3,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Jaime's Daycare, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Leslie Frink's Daycare, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Wind Ridge Schoolhouse, LLC, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Sarah's Tiny Tots Daycare, Aitkin</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Little Footprints Daycare, Gilbert</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Country Kids Daycare, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Krawlers to Kindergarten, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Kids First Day Care, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>T-N-T's Dynamite Kids, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Sara's Child Care, Virginia</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Tiny Turtles Care Center, Eveleth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Heidi's Home Day Care, Proctor</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Jill's Cozy Curriculum, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Barbie's Daycare, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Kids Can Be Kids Daycare, Hibbing</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Laura Lafreniere, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Montessori School of Duluth, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$3,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Amy's Childcare, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>My Little Bumble Bees Daycare LLC, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Dragonfly Childcare, Duluth</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Little Farmers Daycare, Aitkin</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,000</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Busy Bee Day Care, Hibbing</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,200</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Kristen’s Kare Daycare, Hibbing</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$1,200</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Virginia Drop In Daycare, Virginia</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$4,500</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Milestones Daycare, LLC, Virginia</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$4,500</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Brandie’s Little Bear Learning Center, Hibbing</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$4,500</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Children of Grace Childcare & Education Center, Hibbing
                    </td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$4,500</p>
                    </td>
                  </tr>
                  <tr>
                    <td>Kiddy Karousel Child Care Center, Hibbing</td>
                    <td>
                      <p style={{ textAlign: 'right' }}>$4,500</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>
        </Container>

        <Container>
          <h3 className="undervine">
            <span>
              COVID Response Funding Partners
              <Vine />
            </span>
          </h3>

          <p>
            Resources from the following organizations helped support the
            Northland Foundation’s targeted relief grants; Early Childhood
            Wrap-Around Grants; aid for our region’s small businesses including
            grants, loans, and technical assistance; and grants to licensed
            child care providers.
          </p>

          <details>
            <summary>View COVID Response Funding Partners</summary>

            <div>
              <h5>Targeted Response Grants</h5>
              <p>Margaret A. Cargill Philanthropies</p>
              <p>McKnight Foundation</p>
              <p>
                Minnesota Disaster Recovery Fund: Minnesota Council on
                Foundations, Saint Paul and Minnesota Foundation
              </p>

              <h5>Emergency Early Childhood Wrap-around Grants</h5>
              <p>
                Minnesota Department of Education: Early Education - Governor’s
                Emergency Education Relief Fund
              </p>

              <h5>Small Business Assistance</h5>
              <p>
                State of Minnesota: Department of Employment and Economic
                Development
              </p>
              <p>Compeer Financial</p>
              <p>
                COVID-19 Knight Foundation Response Fund of the Duluth Superior
                Area Community Foundation
              </p>
              <p>Wells Fargo Foundation</p>

              <h5>Emergency Child Care Grants</h5>
              <p>Blue Cross and Blue Shield of Minnesota Foundation</p>
              <p>St. Louis County Community Block Grant Program</p>
              <p>Minnesota Power Foundation</p>
              <p>Wells Fargo Foundation</p>
            </div>
          </details>
        </Container>
      </article>
    </>
  );
}
